import type { CSSProperties } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { StoreSyncStatus } from '@testfeed/shopify-contracts'
import { buildSyncHistory, type SyncHistoryDay } from '../../store-intelligence/syncHistoryHeatmapData'

const historyCopy = shopifyComponentCopy.storeIntelligenceSyncHistory

const CELL = 13
const GAP = 4

const FILL: Record<SyncHistoryDay['state'], string> = {
  verified: '#1f883d',
  collecting: '#0969da',
  pending: '#d8e2f3',
  ancient: '#f1f1f1',
  before_history: 'transparent',
  upcoming: 'transparent',
}

const STATE_LABEL_HISTORY: Record<SyncHistoryDay['state'], string> = {
  verified: historyCopy.inYourNumbers006,
  collecting: historyCopy.syncingNow007,
  pending: historyCopy.onItsWay009,
  ancient: historyCopy.keptForRecords014,
  before_history: historyCopy.beforeTestFeedHistory017,
  upcoming: historyCopy.notYet011,
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function buildWeeks(days: SyncHistoryDay[]): (SyncHistoryDay | null)[][] {
  const firstDow = new Date(`${days[0]?.date ?? '2026-07-11'}T00:00:00.000Z`).getUTCDay()
  const weeks: (SyncHistoryDay | null)[][] = []
  let week: (SyncHistoryDay | null)[] = Array.from({ length: firstDow }, () => null)
  for (const day of days) {
    week.push(day)
    if (week.length === 7) {
      weeks.push(week)
      week = []
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null)
    weeks.push(week)
  }
  return weeks
}

function monthLabel(week: (SyncHistoryDay | null)[], previous: (SyncHistoryDay | null)[]): string {
  const first = week.find((day) => day !== null)
  const prevFirst = previous.find((day) => day !== null)
  if (!first) return ''
  const month = new Date(`${first.date}T00:00:00.000Z`).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  if (!prevFirst) return month
  const prevMonth = new Date(`${prevFirst.date}T00:00:00.000Z`).toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  })
  return month === prevMonth ? '' : month
}

function cellStyle(state: SyncHistoryDay['state']): CSSProperties {
  if (state === 'ancient') {
    return {
      width: CELL,
      height: CELL,
      borderRadius: 3,
      background: FILL[state],
      border: '1px dashed rgba(27,31,36,0.25)',
    }
  }
  if (state === 'upcoming' || state === 'before_history') {
    return { width: CELL, height: CELL, borderRadius: 3, background: FILL[state], border: '1px dashed #c4cdd5' }
  }
  return {
    width: CELL,
    height: CELL,
    borderRadius: 3,
    background: FILL[state],
    border: '1px solid rgba(27,31,36,0.08)',
  }
}

/** One verified/pending window as a calendar heat grid, GitHub-style. Renders nothing without coverage. */
export function SettingsSyncHistory({ status }: { status: StoreSyncStatus }) {
  const coverage = status.coverage
  if (!coverage) return null
  const todayKey = new Date().toISOString().slice(0, 10)
  const history = buildSyncHistory({ coverage, syncActive: status.syncActive, todayKey })
  const complete = history.verified >= history.total
  const weeks = buildWeeks(history.days)

  return (
    <s-box border="base" borderRadius="base" padding="base">
      <s-stack direction="block" gap="base">
        <s-stack direction="block" gap="small-200">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-text fontWeight="semibold">Order collection history</s-text>
            <s-text color="subdued">
              {complete
                ? `${history.total} ${historyCopy.daysOfHistoryCollected001}`
                : `${history.verified} ${historyCopy.daysCount002} ${history.total} ${historyCopy.days003}`}
            </s-text>
          </s-stack>
          <s-text color="subdued">
            {complete ? historyCopy.historyComplete004 : historyCopy.fillingAutomatically005}
          </s-text>
        </s-stack>
        <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
          <div style={{ display: 'flex', gap: GAP, maxWidth: 560 }}>
            <div>
              <div style={{ height: 18 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
                {WEEKDAYS.map((day, row) => (
                  <div key={day} style={{ height: CELL, fontSize: 10, color: '#59636e', lineHeight: `${CELL}px` }}>
                    {row % 2 === 1 ? day : ''}
                  </div>
                ))}
              </div>
            </div>
            {weeks.map((week, column) => (
              <div key={column}>
                <div style={{ height: 18, fontSize: 11, color: '#59636e' }}>
                  {monthLabel(week, weeks[column - 1] ?? [])}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
                  {week.map((day, row) =>
                    day === null ? (
                      <div key={row} style={{ width: CELL, height: CELL }} />
                    ) : (
                      <div key={row} style={cellStyle(day.state)} title={day.date} />
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
          {(['verified', 'collecting', 'pending', 'ancient', 'before_history', 'upcoming'] as const).map((state) => (
            <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ ...cellStyle(state), width: 11, height: 11 }} />
              <s-text color="subdued">{STATE_LABEL_HISTORY[state]}</s-text>
            </div>
          ))}
        </div>
      </s-stack>
    </s-box>
  )
}
